import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import PasswordInput from "./input-password";
import { Button } from "@/components/ui/button";
import type { IDialogBox } from "./header";
import { useForm } from "@tanstack/react-form";
import type { IChangePasswordPayload } from "@/hooks/use-changePassword";
import { useMChangePassword } from "@/hooks/use-changePassword";
import { queryClient } from "@/hooks/use-query-client";
import { PASSWORD_REQUIREMENTS_MESSAGE, QueryKeys } from "@/constants";
import type { Demographics } from "@/model/demographics";
import useAuth from "@/context/auth-provider";
import { changePasswordSchema } from "@/lib/form-schema";
import { cn, describeMissingFields } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { ErrorNotificationType } from "./error-notification";
import { useNotificationError } from "@/context/error-provider";
import InfoToolTip from "./info-tooltip";
import ScreenRouteNames from "@/constants/screen-route-names";

const ChangePassword: React.FC<IDialogBox> = ({
  dialogOpen,
  setDialogOpen,
  logDialogName,
  logTrigger,
}) => {
  const navigate = useNavigate();
  const { authData } = useAuth();
  const changePasswordQuery = useMChangePassword();
  const { addError } = useNotificationError();
  const demographicsInfo = queryClient.getQueryData([
    QueryKeys.DEMOGRAPHICS,
    authData?.user?.username,
  ]) as Demographics;

  const defaultValues: IChangePasswordPayload = {
    currentPassword: "",
    newPassword: "",
  };

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      changePasswordQuery.mutate(
        {
          currentPassword: value.currentPassword,
          newPassword: value.newPassword,
          username: demographicsInfo?.username,
          groupId: demographicsInfo?.groupId,
          divisionId: demographicsInfo?.divisionId,
          memberId: demographicsInfo?.memberId,
          accessToken: authData?.user!.access_token,
        },
        {
          onSuccess: (res) => {
            form.reset();
            if (res.summary?.status_message.toLowerCase() === "success") {
              setDialogOpen(false);
              navigate(`${ScreenRouteNames.HOME.path}`, { replace: true });
              addError({
                message: res.data.message,
                type: ErrorNotificationType.INFO,
              });
            } else {
              addError({
                message: res.data.message,
                type: ErrorNotificationType.ERROR,
              });
            }
          },

          onError: (error) => {
            form.reset();
            setDialogOpen(false);
            addError({
              message: error.message,
              type: ErrorNotificationType.ERROR,
            });
          },
        },
      );
    },
  });

  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={(isOpen) => {
        form.reset();
        setDialogOpen(isOpen);
      }}
      logDialogName={logDialogName}
      logTrigger={logTrigger}
    >
      <div className="bg-red-500">
        <DialogContent
          className="sm:max-w-[425px]"
          logDialogName={logDialogName}
        >
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Update your password below.</DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-10"
            method="POST"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <input
              type="text"
              name="username"
              autoComplete="username"
              className="hidden"
              defaultValue={authData?.user?.username ?? ""}
              readOnly
            />
            <div className="grid gap-4">
              <div className="space-y-4">
                <form.Field
                  name="currentPassword"
                  validators={{
                    onBlur: ({ value }) => {
                      const result =
                        changePasswordSchema.shape.password.safeParse(value);
                      return result.success
                        ? undefined
                        : result.error.errors[0].message;
                    },
                  }}
                >
                  {(field) => (
                    <div className="space-y-1">
                      <div className="flex flex-row items-center space-x-1">
                        <Label htmlFor="currentPassword">
                          Current Password
                        </Label>
                        <InfoToolTip info={PASSWORD_REQUIREMENTS_MESSAGE} />
                      </div>
                      <PasswordInput
                        id="currentPassword"
                        autoComplete="current-password"
                        placeholder="Current Password"
                        value={field.state.value}
                        onChange={(newValue) => field.handleChange(newValue)}
                        onBlur={() => field.handleBlur()}
                        error={field.state.meta.errors[0]}
                      />
                    </div>
                  )}
                </form.Field>

                <form.Field
                  name="newPassword"
                  validators={{
                    onBlur: ({ value, fieldApi }) => {
                      const password =
                        fieldApi.form.getFieldValue("currentPassword");
                      if (value === password)
                        return "New password should be different from current password";
                      const result =
                        changePasswordSchema.shape.newPassword.safeParse(value);
                      return result.success
                        ? undefined
                        : result.error.errors[0].message;
                    },
                    onChangeListenTo: ["currentPassword", "newPassword"],
                  }}
                >
                  {(field) => (
                    <div className="space-y-1">
                      <div className="flex flex-row items-center space-x-1">
                        <Label htmlFor="newPassword">New Password</Label>
                        <InfoToolTip info={PASSWORD_REQUIREMENTS_MESSAGE} />
                      </div>
                      <PasswordInput
                        id="newPassword"
                        autoComplete="new-password"
                        placeholder="New Password"
                        value={field.state.value}
                        onChange={(newValue) => field.handleChange(newValue)}
                        onBlur={() => field.handleBlur()}
                        error={field.state.meta.errors[0]}
                      />
                    </div>
                  )}
                </form.Field>
                <form.Field
                  name="confirmNewPassword"
                  validators={{
                    onBlur: ({ value, fieldApi }) => {
                      const newPassword =
                        fieldApi.form.getFieldValue("newPassword");
                      if (value !== newPassword) return "Passwords donot match";
                      const result =
                        changePasswordSchema.shape.confirmPassword.safeParse(
                          value,
                        );
                      if (!result.success) {
                        return result.error.issues[0]?.message;
                      }
                      const password =
                        fieldApi.form.getFieldValue("newPassword");
                      if (password && value !== password) {
                        return "Passwords do not match";
                      }
                      return undefined;
                    },
                    onChangeListenTo: ["newPassword", "confirmNewPassword"],
                  }}
                >
                  {(field) => (
                    <div className="space-y-1">
                      <Label htmlFor="confirmNewPassword">
                        Confirm New Password
                      </Label>
                      <PasswordInput
                        id="confirmNewPassword"
                        autoComplete="new-password"
                        value={field.state.value}
                        placeholder="New Password Again"
                        onChange={(newValue) => field.handleChange(newValue)}
                        onBlur={() => field.handleBlur()}
                        error={field.state.meta.errors[0]}
                      />
                    </div>
                  )}
                </form.Field>
              </div>
            </div>

            <DialogFooter>
              <DialogClose asChild logDialogName={logDialogName}>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <form.Subscribe
                selector={(state) => [
                  state.canSubmit,
                  state.values.currentPassword,
                  state.values.newPassword,
                  state.values.confirmNewPassword,
                ]}
              >
                {([
                  canSubmit,
                  currentPassword,
                  newPassword,
                  confirmNewPassword,
                ]) => {
                  // 3.3.1 Error Identification — A: names the specific empty
                  // field(s) rather than a static list.
                  const requirement =
                    describeMissingFields([
                      { label: "current password", value: currentPassword },
                      { label: "new password", value: newPassword },
                      {
                        label: "confirm new password",
                        value: confirmNewPassword,
                      },
                    ]) || "Disabled. Fix the errors above to continue.";
                  return (
                    <>
                      <Button
                        className={cn(
                          "bg-primary hover:bg-primary-selected",
                          !canSubmit && "opacity-50",
                        )}
                        type="submit"
                        disabled={changePasswordQuery.isPending}
                        aria-disabled={!canSubmit}
                        aria-describedby={
                          !canSubmit ? "change-password-requirement" : undefined
                        }
                      >
                        {changePasswordQuery.isPending
                          ? "Changing..."
                          : "Change Password"}
                      </Button>
                      {!canSubmit && (
                        <p id="change-password-requirement" className="sr-only">
                          {requirement}
                        </p>
                      )}
                    </>
                  );
                }}
              </form.Subscribe>
            </DialogFooter>
          </form>
        </DialogContent>
      </div>
    </Dialog>
  );
};

export default ChangePassword;
